import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

import { CustomerService, PartnerService, UserService } from '../services';
import { AuthStore, SelectedCustomerStore, SelectedPartnerStore } from '../stores';
import { initialNavbarState, NavbarState } from '../models/state/navbar.state';
import { Customer, UserUpdateRequest } from '../models';
import { LanguageEnum } from '../enums';

export const NavbarStore = signalStore(
  withState<NavbarState>(initialNavbarState),
  withMethods(
    (
      store,
      customerService = inject(CustomerService),
      partnerService = inject(PartnerService),
      userService = inject(UserService),
      authStore = inject(AuthStore),
      selectedCustomerStore = inject(SelectedCustomerStore),
      selectedPartnerStore = inject(SelectedPartnerStore),
    ) => {
      const selectPartner = rxMethod<string>(
        pipe(
          switchMap((partnerId) =>
            partnerService.getPartnerByRole(partnerId).pipe(
              tap((partner) => selectedPartnerStore.setSelectedPartner(partner)),
              catchError(() => {
                return of();
              }),
            ),
          ),
        ),
      );
      const selectCustomer = rxMethod<number>(
        pipe(
          switchMap((customerId) =>
            customerService.getCustomerByRole(customerId).pipe(
              tap((customer) => selectedCustomerStore.setSelectedCustomer(customer)),
              catchError(() => {
                return of();
              }),
            ),
          ),
        ),
      );
      const loadCustomers = rxMethod<string | undefined>(
        pipe(
          map((searchText) => {
            const customerPageRequest = { page: 0, size: 20, searchText };
            patchState(store, { customerPageRequest, loading: true, customers: [] });
            return customerPageRequest;
          }),
          switchMap((pageRequest) =>
            customerService.getCustomersLightByRole(pageRequest).pipe(
              tap((customers) => {
                const selectedCustomer = selectedCustomerStore.selectedCustomer();
                if (!selectedCustomer) {
                  if (customers.length) {
                    selectedCustomerStore.setSelectedCustomer(customers[0] as Customer);
                  }
                } else {
                  const index = customers.findIndex((c) => c.id === selectedCustomer.id);
                  if (index !== -1) {
                    customers.splice(index, 1);
                  }
                  customers.unshift(selectedCustomer);
                }
                patchState(store, {
                  customers,
                  completed: customers.length < (pageRequest.size ?? 20),
                  loading: false,
                });
              }),
              catchError(() => {
                patchState(store, { loading: false });
                return of();
              }),
            ),
          ),
        ),
      );
      const loadPartners = rxMethod<string | undefined>(
        pipe(
          map((searchText) => {
            const partnerPageRequest = { page: 0, size: 20, searchText };
            patchState(store, { partnerPageRequest, partnerLoading: true, partners: [] });
            return partnerPageRequest;
          }),
          switchMap((pageRequest) =>
            partnerService.getPartnersByRole(pageRequest).pipe(
              tap((partners) => {
                const selectedPartner = selectedPartnerStore.selectedPartner();
                if (!selectedPartner) {
                  if (partners.length) {
                    selectedPartnerStore.setSelectedPartner(partners[0]);
                  }
                } else {
                  const index = partners.findIndex((c) => c.id === selectedPartner.id);
                  if (index !== -1) {
                    partners.splice(index, 1);
                  }
                  partners.unshift(selectedPartner);
                }
                patchState(store, {
                  partners,
                  partnerCompleted: partners.length < (pageRequest.size ?? 20),
                  partnerLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { partnerLoading: false });
                return of();
              }),
            ),
          ),
        ),
      );
      const loadMoreCustomers = rxMethod<void>(
        pipe(
          map(() => {
            const { page, ...restOfPageRequest } = store.customerPageRequest();
            const customerPageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { customerPageRequest, loading: true });
            return customerPageRequest;
          }),
          concatMap((pageRequest) =>
            customerService.getCustomersLightByRole(pageRequest).pipe(
              tap((customers) =>
                patchState(store, {
                  customers: [...store.customers(), ...customers],
                  completed: customers.length < (pageRequest.size ?? 20),
                  loading: false,
                }),
              ),
              catchError(() => {
                patchState(store, { loading: false });
                return of();
              }),
            ),
          ),
        ),
      );
      const loadMorePartners = rxMethod<void>(
        pipe(
          map(() => {
            const { page, ...restOfPageRequest } = store.partnerPageRequest();
            const partnerPageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { partnerPageRequest, partnerLoading: true });
            return partnerPageRequest;
          }),
          concatMap((pageRequest) =>
            partnerService.getPartnersByRole(pageRequest).pipe(
              tap((partners) =>
                patchState(store, {
                  partners: [...store.partners(), ...partners],
                  partnerCompleted: partners.length < (pageRequest.size ?? 20),
                  partnerLoading: false,
                }),
              ),
              catchError(() => {
                patchState(store, { loading: false });
                return of();
              }),
            ),
          ),
        ),
      );
      const changeCurrentUserLanguage = rxMethod<LanguageEnum>(
        switchMap((language) =>
          userService.updateCurrentUser({ language } as UserUpdateRequest).pipe(catchError(() => of())),
        ),
      );
      return {
        selectPartner,
        selectCustomer,
        loadCustomers,
        loadMoreCustomers,
        loadPartners,
        loadMorePartners,
        changeCurrentUserLanguage,
      };
    },
  ),
);
